import { t } from 'i18next';
import { Trash2Icon } from 'lucide-react';
import { useWatch, type Control, type Path } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { MultiSelectField } from '@/components/atoms/MultiSelect';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { CustomFieldType } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { fieldOptions, Form, requiredOptions } from '../VisitorInformation';

interface LanguageOption {
  label: string;
  value: string;
}

interface FormField {
  field: string;
  required: boolean;
  languages?: { label: string; value: string }[];
  field_label?: string;
  variable?: string;
  variable_type?: string;
}

type Props = {
  index: number;
  onDelete: () => void;
  control?: Control<Form>;
  languageOptions?: LanguageOption[];
  fieldTypes?: FormField['field'][];
};

/**
 * VisitorFormCard component for managing individual form fields in the visitor information section.
 *
 * @param index - The index of the field in the fields array
 * @param onDelete - Callback function to delete the field
 * @param control - React Hook Form control object
 * @param languageOptions - Available language options for language fields
 * @param fieldTypes - The type of every field, to check for duplicates
 */
const VisitorFormCard = ({
  index,
  onDelete,
  control,
  languageOptions = [],
  fieldTypes = [],
}: Props) => {
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const fieldType = useWatch({ control, name: `fields.${index}.field` as Path<Form> });
  const isCustom = fieldType === 'custom';
  const isLanguage = fieldType === 'language';

  /**
   * Determines if a field option should be disabled based on whether it's already used.
   * Only 'custom' field types can be repeated, all other field types can only be used once.
   *
   * @param optionValue - The field type value to check
   * @returns true if the option should be disabled, false otherwise
   */
  const isOptionDisabled = (optionValue: string): boolean => {
    if (optionValue === 'custom') {
      return false; // Custom fields can always be repeated
    }

    // Check if this field type is already used in other fields
    return fieldTypes.some(
      (type, fieldIndex) => fieldIndex !== index && type === optionValue && type !== ''
    );
  };

  return (
    <Card className="relative rounded border border-border bg-background pt-8 shadow-none">
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="col-span-2">
            <FormField
              control={control}
              name={`fields.${index}.field` as Path<Form>}
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>{t('integrations.visitor.select_field')}</FormLabel>
                  <FormControl>
                    <Select
                      disabled={!canWrite}
                      value={field.value as string}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger
                        id={`field-${index}`}
                        className={cn('bg-background', { 'border-warning': error })}
                      >
                        <SelectValue placeholder={t('integrations.visitor.select_field')} />
                      </SelectTrigger>
                      <SelectContent>
                        {fieldOptions.map((option) => {
                          const isDisabled = isOptionDisabled(option.value);
                          return (
                            <SelectItem
                              key={option.value}
                              value={option.value}
                              disabled={isDisabled}
                            >
                              {option.label_key}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="col-span-2 md:col-span-1">
            <FormField
              control={control}
              name={`fields.${index}.required` as Path<Form>}
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>{t('common.required')}</FormLabel>
                  <FormControl>
                    <Select
                      disabled={!canWrite}
                      value={field.value.toString()}
                      onValueChange={(value) => field.onChange(value === 'true')}
                    >
                      <SelectTrigger
                        id={`required-${index}`}
                        className={cn('bg-background', { 'border-warning': error })}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {requiredOptions.map((option) => (
                          <SelectItem key={option.value.toString()} value={option.value.toString()}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {isCustom && (
          <div className="space-y-4">
            <FormField
              control={control}
              name={`fields.${index}.field_label` as Path<Form>}
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value as string}
                      id={`custom-${index}`}
                      placeholder={t('integrations.visitor.label_placeholder')}
                      readOnly={!canWrite}
                      onBlur={field.onBlur}
                      className={cn('bg-background', { 'border-warning': error })}
                    />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>{t('integrations.visitor.label_subtitle')}</FormDescription>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <FormField
                  control={control}
                  name={`fields.${index}.variable` as Path<Form>}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value as string}
                          id={`custom-variable-${index}`}
                          placeholder={t('integrations.visitor.variable_name_placeholder')}
                          readOnly={!canWrite}
                          className={cn('bg-background', { 'border-warning': error })}
                        />
                      </FormControl>
                      <FormMessage />
                      <FormDescription>
                        {t('integrations.visitor.variable_name_subtitle')}
                      </FormDescription>
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-1">
                <FormField
                  control={control}
                  name={`fields.${index}.variable_type` as Path<Form>}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormControl>
                        <Select
                          disabled={!canWrite}
                          value={field.value as string}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            id={`custom-type-${index}`}
                            className={cn('bg-background', { 'border-warning': error })}
                          >
                            <SelectValue
                              placeholder={t('integrations.visitor.custom_field_type.placeholder')}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(CustomFieldType).map((type) => (
                              <SelectItem key={type} value={type}>
                                {t(`integrations.visitor.custom_field_type.${type}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {isLanguage && languageOptions && languageOptions.length > 0 && (
          <FormField
            control={control}
            name={`fields.${index}.languages`}
            render={({ field, fieldState: { error } }) => (
              <FormItem className="pt-4">
                <FormControl>
                  <MultiSelectField
                    title={t('integrations.visitor.languages')}
                    options={languageOptions}
                    value={field.value?.map((lang: LanguageOption) => lang.value) || []}
                    onChange={(selectedValues) => {
                      const selectedLanguages = selectedValues
                        .map((value) => languageOptions.find((option) => option.value === value))
                        .filter(Boolean);
                      field.onChange(selectedLanguages);
                    }}
                    disabled={!canWrite}
                    error={Boolean(error)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="absolute top-2 right-2">
          <Button
            type="button"
            variant="ghost-neutral"
            size="icon-sm"
            onClick={onDelete}
            disabled={!canWrite}
            className="hover:text-destructive hover:[&_svg]:text-destructive"
            aria-label={t('common.remove_field')}
          >
            <Trash2Icon size={16} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default VisitorFormCard;
